import { useMemo } from "react";
import { useContent } from "contexts/contentContext/contentContext";
import { useUser } from "contexts/userContext/userContext";
import { eurList, nisList } from "./Lists";
import type { BillingDetailsT } from "../../../../models/models";

/**
 * Custom hook that returns the current currency based on user data and billing details.
 * If the billing currency is available, it returns the billing currency.
 * Otherwise, it determines the currency based on the user's country.
 *
 * @returns The current currency (either the billing currency or the user's country currency).
 */
const useCurrentCurrency = () => {
  const { getUserData, getUserBillingDetails } = useUser();
  const { getBasicSettingsData } = useContent();

  const billingDetails = getUserBillingDetails() as BillingDetailsT;

  // Currency of the last payment
  const billingCurrency = billingDetails?.currency;

  const currentUserCurrency = useMemo(() => {
    const countryName =
      getUserData()?.UserCountry || getBasicSettingsData()?.userCountry;
    if (nisList.includes(countryName)) return "ILS";
    if (eurList.includes(countryName)) return "EUR";
    return "USD";
  }, [getUserData(), getBasicSettingsData()]);

  if (billingCurrency) {
    // If the billing currency is NIS, the currency should be displayed as ILS
    const fixedBillingCurrency =
      billingCurrency === "NIS" ? "ILS" : billingCurrency;
    return fixedBillingCurrency;
  }

  return currentUserCurrency;
};

export default useCurrentCurrency;
